import {
  Alert,
  Button,
  Card,
  Field,
  Modal,
  PageHeader,
  Skeleton,
  StatusBadge,
  Tab,
  TabList,
  TabPanel,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
  Tabs,
  useToast,
} from '@catering/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, getRouteApi } from '@tanstack/react-router';
import { ClipboardList, History, Pencil, RefreshCcw, Save, Send, XCircle } from 'lucide-react';
import { type JSX, useState } from 'react';

import { ApiError } from '../../api/client';
import { useCurrentUser } from '../../auth/use-current-user';
import { formatCalendarDate } from '../../orders/format-calendar-date';
import {
  cancelOrder,
  getOrder,
  submitOrder,
  updateOrder,
  type Order,
  type OrderUpdateInput,
} from '../../orders/api';
import { isCutoffPassedError, mapOrderError } from '../../orders/order-api-errors';
import { priceSourceLabel } from '../../orders/price-source';
import { legacyOrderItems, toOrderFormValues, toOrderUpdateInput } from '../../orders/order-form-schema';
import { hasPermission } from '../../auth/has-permission';
import { OrderChangeRequestsTab } from './components/order-change-requests-tab';
import { OrderForm } from './components/order-form';
import { OrderHistoryTab } from './components/order-history-tab';

export type OrderDetailTab = 'detalle' | 'historial' | 'cambios';

const orderDetailRoute = getRouteApi('/protected/orders/$orderId');

const TAB_LABELS: Record<OrderDetailTab, string> = {
  detalle: 'Detalle',
  historial: 'Historial',
  cambios: 'Cambios',
};

const TAB_ICONS: Record<OrderDetailTab, typeof ClipboardList> = {
  detalle: ClipboardList,
  historial: History,
  cambios: RefreshCcw,
};

/** `design.md` §5: `Order` is editable only while `DRAFT`/`SUBMITTED`, the server's own gate this mirrors, never the only barrier. */
function isEditable(status: Order['status']): boolean {
  return status === 'DRAFT' || status === 'SUBMITTED';
}

function money(cents: number): string {
  return (cents / 100).toLocaleString('es-AR', { style: 'currency', currency: 'ARS' });
}

/**
 * `orders/spec.md` → "Pantallas de pedido en back-office". Tabs are local
 * state driven by the `tab` search param, same convention as
 * `ContractDetailPage`. Pestaña Detalle: líneas congeladas (snapshot,
 * sin edición de precio/nombre) y transición a "Enviar"/"Cancelar" según
 * `isEditable()`/permisos (`design.md` §5's transition table). Historial
 * y Cambios llegan en PR 6 (`tasks.md` PR 5's own scope note).
 */
export function OrderDetailPage(): JSX.Element {
  const { orderId } = orderDetailRoute.useParams();
  const { tab } = orderDetailRoute.useSearch();
  const navigate = orderDetailRoute.useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const currentUser = useCurrentUser();

  const [editing, setEditing] = useState(false);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [submitConfirmOpen, setSubmitConfirmOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');

  const query = useQuery({ queryKey: ['order', orderId], queryFn: () => getOrder(orderId) });

  async function invalidate(): Promise<void> {
    await queryClient.invalidateQueries({ queryKey: ['order', orderId] });
    await queryClient.invalidateQueries({ queryKey: ['orders'] });
  }

  const updateMutation = useMutation({
    mutationFn: (input: OrderUpdateInput) => updateOrder(orderId, input),
    onSuccess: async () => {
      await invalidate();
      setEditing(false);
      toast('success', 'Pedido actualizado');
    },
  });

  const submitMutation = useMutation({
    mutationFn: () => submitOrder(orderId),
    onSuccess: async () => {
      await invalidate();
      toast('success', 'Pedido enviado');
    },
    onSettled: () => setSubmitConfirmOpen(false),
  });

  const cancelMutation = useMutation({
    mutationFn: (reason: string) => cancelOrder(orderId, reason),
    onSuccess: async () => {
      await invalidate();
      setCancelModalOpen(false);
      setCancelReason('');
      toast('success', 'Pedido cancelado');
    },
  });

  function goToTab(next: OrderDetailTab): void {
    void navigate({ search: (prev) => ({ ...prev, tab: next }) });
  }

  if (query.isPending) {
    return (
      <div className="flex flex-col gap-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  if (query.isError) {
    if (query.error instanceof ApiError && query.error.status === 404) {
      return (
        <Alert tone="danger" title="Pedido no encontrado">
          <Link to="/orders" className="underline">
            Volver al listado
          </Link>
        </Alert>
      );
    }

    return (
      <Alert tone="danger" title="No pudimos cargar el pedido">
        <div className="flex items-center justify-between gap-4">
          <span>Ocurrió un error al cargar. Intentá de nuevo.</span>
          <Button variant="secondary" size="sm" onClick={() => void query.refetch()}>
            Reintentar
          </Button>
        </div>
      </Alert>
    );
  }

  const order = query.data;
  const canUpdate = hasPermission(currentUser.data, 'order.update');
  const canCancel = hasPermission(currentUser.data, 'order.cancel');
  const canOverride = hasPermission(currentUser.data, 'order.override.cutoff');
  const editable = isEditable(order.status);
  const cutoffError = isCutoffPassedError(updateMutation.error);
  const updateErrorMessage = cutoffError ? undefined : mapOrderError(updateMutation.error);

  return (
    <section className="flex flex-col gap-6">
      <PageHeader
        icon={ClipboardList}
        title={order.orderNumber}
        description={`${order.customerName} · ${order.siteName} · ${formatCalendarDate(order.serviceDate)}`}
        actions={<StatusBadge domain="order" status={order.status} />}
      />

      <Tabs value={tab} onValueChange={(value) => goToTab(value as OrderDetailTab)}>
        <TabList aria-label="Pestañas del pedido">
          {(Object.keys(TAB_LABELS) as OrderDetailTab[]).map((key) => (
            <Tab key={key} value={key} icon={TAB_ICONS[key]}>
              {TAB_LABELS[key]}
            </Tab>
          ))}
        </TabList>

        <TabPanel value="detalle">
          {editing ? (
            <Card>
              <OrderForm
                mode="edit"
                identity={{
                  customerName: order.customerName,
                  siteName: order.siteName,
                  serviceDate: order.serviceDate,
                }}
                defaultValues={toOrderFormValues(order)}
                legacyItems={legacyOrderItems(order)}
                submitting={updateMutation.isPending}
                submitLabel="Guardar cambios"
                submitIcon={Save}
                formError={updateErrorMessage === '' ? undefined : updateErrorMessage}
                cutoffError={cutoffError}
                canOverrideCutoff={canOverride}
                onCancel={() => setEditing(false)}
                onSubmit={async (values) => {
                  await updateMutation
                    .mutateAsync(toOrderUpdateInput(values))
                    .catch(() => undefined);
                }}
              />
            </Card>
          ) : (
            <div className="flex flex-col gap-6">
              {submitMutation.isError ? (
                <Alert tone="danger">{mapOrderError(submitMutation.error)}</Alert>
              ) : null}

              <Card>
                <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
                  <h2 className="text-base font-semibold text-slate-900">Líneas del pedido</h2>
                  {editable && canUpdate ? (
                    <Button
                      variant="secondary"
                      size="sm"
                      icon={Pencil}
                      onClick={() => setEditing(true)}
                    >
                      Editar
                    </Button>
                  ) : null}
                </div>

                <Table>
                  <caption className="sr-only">Líneas del pedido</caption>
                  <thead>
                    <tr>
                      <TableHeader>Posición</TableHeader>
                      <TableHeader>Menú</TableHeader>
                      <TableHeader>Cantidad</TableHeader>
                      <TableHeader>Precio unitario</TableHeader>
                      <TableHeader>Subtotal</TableHeader>
                      <TableHeader>Notas</TableHeader>
                    </tr>
                  </thead>
                  <TableBody>
                    {order.items.map((item) => (
                      <TableRow key={item.id}>
                        <TableCell>{item.position?.name ?? '—'}</TableCell>
                        <TableCell>{item.menuNameSnapshot}</TableCell>
                        <TableCell>{item.quantity}</TableCell>
                        <TableCell>
                          {money(item.unitPriceCents)}
                          <span
                            aria-label="Origen del precio"
                            className="block text-xs text-slate-500"
                          >
                            {priceSourceLabel(item.priceSource)}
                          </span>
                        </TableCell>
                        <TableCell>{money(item.subtotalCents)}</TableCell>
                        <TableCell>{item.notes ?? '—'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>

                <div className="mt-4 flex justify-end gap-6 text-sm text-slate-700">
                  <span>Subtotal: {money(order.subtotalCents)}</span>
                  <span>IVA: {money(order.vatCents)}</span>
                  <span className="font-semibold text-slate-900">
                    Total: {money(order.totalCents)}
                  </span>
                </div>
              </Card>

              {editable && (canUpdate || canCancel) ? (
                <div className="flex items-center gap-3">
                  {order.status === 'DRAFT' && canUpdate ? (
                    <Button
                      icon={Send}
                      onClick={() => setSubmitConfirmOpen(true)}
                    >
                      Enviar
                    </Button>
                  ) : null}
                  {canCancel ? (
                    <Button
                      variant="secondary"
                      icon={XCircle}
                      onClick={() => setCancelModalOpen(true)}
                    >
                      Cancelar pedido
                    </Button>
                  ) : null}
                </div>
              ) : null}
            </div>
          )}
        </TabPanel>

        <TabPanel value="historial">
          <OrderHistoryTab orderId={order.id} />
        </TabPanel>

        <TabPanel value="cambios">
          <OrderChangeRequestsTab order={order} currentUser={currentUser.data} />
        </TabPanel>
      </Tabs>

      {submitConfirmOpen ? (
        <Modal
          open
          onClose={() => setSubmitConfirmOpen(false)}
          title="Enviar pedido"
          className="max-w-md"
        >
          <div className="flex flex-col gap-4">
            <p className="text-sm text-slate-700">
              Una vez enviado, el pedido ya no vuelve a borrador. ¿Querés enviarlo?
            </p>
            <div className="flex items-center gap-3">
              <Button loading={submitMutation.isPending} onClick={() => submitMutation.mutate()}>
                Confirmar envío
              </Button>
              <Button variant="secondary" onClick={() => setSubmitConfirmOpen(false)}>
                Volver
              </Button>
            </div>
          </div>
        </Modal>
      ) : null}

      {cancelModalOpen ? (
        <Modal
          open
          onClose={() => setCancelModalOpen(false)}
          title="Cancelar pedido"
          className="max-w-md"
        >
          <div className="flex flex-col gap-4">
            {cancelMutation.isError ? (
              <Alert tone="danger">{mapOrderError(cancelMutation.error)}</Alert>
            ) : null}
            <Field
              label="Motivo de la cancelación"
              name="cancellationReason"
              required
              value={cancelReason}
              onChange={(event) => setCancelReason(event.target.value)}
            />
            <div className="flex items-center gap-3">
              <Button
                loading={cancelMutation.isPending}
                disabled={cancelReason.trim() === ''}
                onClick={() => cancelMutation.mutate(cancelReason)}
              >
                Confirmar cancelación
              </Button>
              <Button variant="secondary" onClick={() => setCancelModalOpen(false)}>
                Volver
              </Button>
            </div>
          </div>
        </Modal>
      ) : null}
    </section>
  );
}
